import React, { useState, useEffect, useMemo } from 'react';
import { Search, Save, CheckSquare, Square, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiSetup';

const GISConfiguration = () => {
  const { hasPermission, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [gradeFilter, setGradeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(buildApiUrl('/api/v1/gis/employees'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch GIS employees');
      }
      
      const res = await response.json();
      if (res.status === 'success' && Array.isArray(res.data)) {
        setEmployees(res.data);
        
        // Initialize selected IDs for those who are active
        const initialSelected = new Set();
        res.data.forEach(emp => {
          if (emp.gis_status === 'active') {
            initialSelected.add(emp.employee_id);
          }
        });
        setSelectedIds(initialSelected);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load GIS configuration data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(buildApiUrl('/api/v1/gis/employees'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          employee_ids: Array.from(selectedIds)
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save GIS assignments');
      }
      
      const res = await response.json();
      setSuccess(res.message || 'GIS assignments saved successfully.');
      
      // Update local state to reflect new status
      setEmployees(employees.map(emp => ({
        ...emp,
        gis_status: selectedIds.has(emp.employee_id) ? 'active' : 'inactive'
      })));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to save GIS configuration.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEmployee = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Filtered employees
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      // Text search
      const matchesSearch = 
        !searchTerm || 
        emp.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.emp_code.toLowerCase().includes(searchTerm.toLowerCase());
        
      // Dropdown filters
      const matchesDept = deptFilter === 'ALL' || emp.department === deptFilter;
      const matchesGrade = gradeFilter === 'ALL' || emp.grade === gradeFilter;
      const matchesStatus = statusFilter === 'ALL' || 
        (statusFilter === 'ACTIVE' && selectedIds.has(emp.employee_id)) ||
        (statusFilter === 'INACTIVE' && !selectedIds.has(emp.employee_id));
        
      return matchesSearch && matchesDept && matchesGrade && matchesStatus;
    });
  }, [employees, searchTerm, deptFilter, gradeFilter, statusFilter, selectedIds]);

  const handleSelectAll = () => {
    const newSelected = new Set(selectedIds);
    filteredEmployees.forEach(emp => {
      newSelected.add(emp.employee_id);
    });
    setSelectedIds(newSelected);
  };

  const handleDeselectAll = () => {
    const newSelected = new Set(selectedIds);
    filteredEmployees.forEach(emp => {
      newSelected.delete(emp.employee_id);
    });
    setSelectedIds(newSelected);
  };

  // Unique lists for filters
  const departments = [...new Set(employees.map(e => e.department))].filter(Boolean).sort();
  const grades = [...new Set(employees.map(e => e.grade))].filter(Boolean).sort();

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin inline-block w-8 h-8 border-[3px] border-current border-t-transparent text-blue-600 rounded-full mb-2"></div>
        <p>Loading GIS Configuration...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">GIS Employee Configuration</h2>
            <p className="text-sm text-gray-500">Select employees who are eligible for Group Insurance Scheme deductions.</p>
          </div>
          {hasPermission('gis:save') && (
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors ${
                saving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
        
        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> {error}
          </div>
        )}
        
        {success && (
          <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" /> {success}
          </div>
        )}
      </div>

      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search employee..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
          >
            <option value="ALL">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          
          <select 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={gradeFilter}
            onChange={(e) => setGradeFilter(e.target.value)}
          >
            <option value="ALL">All Grades</option>
            {grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>

          <select 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">GIS Active</option>
            <option value="INACTIVE">GIS Inactive</option>
          </select>
        </div>
      </div>

      <div className="px-6 py-3 bg-gray-50 flex items-center gap-4 text-sm font-medium border-b border-gray-200">
        <button onClick={handleSelectAll} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <CheckSquare className="w-4 h-4" /> Select All Filtered
        </button>
        <button onClick={handleDeselectAll} className="text-gray-600 hover:text-gray-800 flex items-center gap-1">
          <Square className="w-4 h-4" /> Deselect All Filtered
        </button>
        <div className="ml-auto text-gray-500">
          Showing {filteredEmployees.length} of {employees.length} employees ({selectedIds.size} selected total)
        </div>
      </div>

      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-left text-sm text-gray-700">
          <thead className="bg-gray-50 text-gray-600 sticky top-0 shadow-sm">
            <tr>
              <th className="px-6 py-3 font-semibold w-16">Select</th>
              <th className="px-6 py-3 font-semibold">Employee Code</th>
              <th className="px-6 py-3 font-semibold">Employee Name</th>
              <th className="px-6 py-3 font-semibold">Department</th>
              <th className="px-6 py-3 font-semibold">Designation</th>
              <th className="px-6 py-3 font-semibold">Grade</th>
              <th className="px-6 py-3 font-semibold">GIS Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  No employees found matching the filters.
                </td>
              </tr>
            ) : (
              filteredEmployees.map((emp) => {
                const isSelected = selectedIds.has(emp.employee_id);
                return (
                  <tr 
                    key={emp.employee_id} 
                    className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                    onClick={() => toggleEmployee(emp.employee_id)}
                  >
                    <td className="px-6 py-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by tr onClick
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                    </td>
                    <td className="px-6 py-3 font-medium text-gray-900">{emp.emp_code}</td>
                    <td className="px-6 py-3">{emp.employee_name}</td>
                    <td className="px-6 py-3 text-gray-500">{emp.department}</td>
                    <td className="px-6 py-3 text-gray-500">{emp.designation}</td>
                    <td className="px-6 py-3 font-medium">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs">{emp.grade}</span>
                    </td>
                    <td className="px-6 py-3">
                      {isSelected ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Active</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-semibold">Inactive</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GISConfiguration;
